<template>
  <div
    ref="pillDropdown"
    class="pill-dropdown"
    :style="[primaryButtonColors, secondaryButtonColors]"
  >
    <div class="dropdown-options">
      <button
        ref="dropdown-trigger"
        class="dropdown-trigger"
        :aria-expanded="isDropdownOpen"
        aria-haspopup="listbox"
        :aria-label="$t('search.filterDropdownAriaLabel', { filterName: name, selectedCount: appliedOptions.length })"
        :aria-labelledby="isDropdownOpen ? 'options-label' : undefined"
        :aria-controls="isDropdownOpen ? 'options-list' : undefined"
        :class="appliedOptions.length > 0 ? 'has-selected-vals' : ''"
        @click="toggleDropdown"
        @keydown="handleTriggerKeydown"
        @focus="emit('closeSuggestionsDropdown')"
      >
        <span class="name">{{ dynamicButtonName }}</span>
        <Icon
          name="lucide:chevron-down"
          size="14"
          aria-hidden="true"
        />
      </button>

      <label
        id="options-label"
        class="sr-only"
      >
        {{ name }} {{ $t('search.options') }}
      </label>
      <div
        v-if="isDropdownOpen"
        class="options-list"
        role="listbox"
        aria-labelledby="options-label"
        :aria-multiselectable="true"
        :aria-activedescendant="accessibility.activeDescendantId.value"
        tabindex="0"
        :style="dropdownHeight"
        @keydown="handleListboxKeydown"
        @focus="handleListboxFocus"
        @blur="handleListboxBlur"
      >
        <div
          v-if="showSearch"
          class="search-section"
        >
          <div class="search-box">
            <Icon
              name="lucide:search"
              size="14"
              aria-hidden="true"
              class="search-icon"
            />
            <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              class="search-input"
              :placeholder="`Search ${name.toLowerCase()}...`"
              @click.stop
              @keydown.tab.exact="focusOnList"
              @keydown.escape.exact="closeDropdown"
              @keydown.shift.tab.exact="closeDropdown"
            />
          </div>
        </div>
        <ul
          class="options-list-ul"
          role="presentation"
        >
          <li
            v-for="(option, index) in pillOptions"
            :id="`option-${index}`"
            :ref="`option-${index}`"
            :key="index"
            role="option"
            :aria-selected="pillOptions[index].checked"
            :aria-checked="pillOptions[index].checked"
            :class="{
              'show-checkbox': name === 'Schools' || name === 'Type',
              focused: accessibility.focusedIndex.value === index,
            }"
            @click.prevent.stop="checkOption(index)"
          >
            {{ option.name }}
          </li>
        </ul>
        <div
          v-if="allowMultiSelect"
          class="clear-section"
        >
          <button
            class="clear-button"
            :disabled="selectedOptions.length === 0"
            :aria-label="$t('search.clearFiltersAriaLabel', { filterName: name })"
            @click.prevent.stop="clearFilters"
            @keydown.enter.prevent.stop="clearFilters"
            @keydown.space.prevent.stop="clearFilters"
          >
            {{ $t('search.clear') }}
          </button>
          <button
            class="apply-button"
            :disabled="!hasUnappliedChanges"
            :aria-label="$t('search.applyFiltersAriaLabel', { filterName: name })"
            @click="applyFilters"
            @keydown.enter.prevent.stop="applyFilters"
            @keydown.space.prevent.stop="applyFilters"
            @blur="handleActionButtonBlur"
          >
            {{ $t('search.apply') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onClickOutside, useArrayDifference } from '@vueuse/core';
import { useThemedButtons } from '@/composables/useThemedButtons';
import { useAccessibleDropdown } from '@/composables/useAccessibleDropdown';
import { useSearchResultsContainer } from '@/composables/useSearchResultsContainer';

const { primaryButtonColors, secondaryButtonColors } = useThemedButtons();
const { getContainerHeight, hasResults, resultsCount } = useSearchResultsContainer();

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
  },
  name: {
    type: String,
    default: '',
  },
  allowMultiSelect: {
    type: Boolean,
    default: true,
  },
  showSearch: {
    type: Boolean,
    default: false,
  },
  dynamicHeight: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['closeSuggestionsDropdown', 'clearFilters', 'applyFilters', 'filterApplied']);

const isDropdownOpen = ref(false);
const searchQuery = ref('');
const pillDropdown = useTemplateRef('pillDropdown');
const searchInput = useTemplateRef('searchInput');

// Use the options prop directly - it's already reactive
const allOptions = computed(() => props.options);
const selectedOptions = computed(() => allOptions.value.filter((option) => option.checked === true));
const appliedOptions = ref([]);

// Watch for external filter clearing by detecting when ALL options are unchecked simultaneously
watch(
  () => props.options,
  (newOptions) => {
    // Si no hay opciones marcadas, limpiar appliedOptions
    if (newOptions.every((option) => !option.checked)) {
      appliedOptions.value = [];
    }
  },
  { deep: true },
);

// Filter options based on search query
const pillOptions = computed(() => {
  if (!props.showSearch || !searchQuery.value.trim()) {
    return allOptions.value;
  }

  return allOptions.value.filter((option) => option.name.toLowerCase().includes(searchQuery.value.toLowerCase()));
});

// Calculate dropdown height based on actual container height
const dropdownHeight = computed(() => {
  if (!props.dynamicHeight) {
    return { '--dropdown-height': 'auto' };
  }

  if (!hasResults.value) {
    return { '--dropdown-height': '200px' };
  }

  if (resultsCount.value <= 3) {
    return { '--dropdown-height': '200px' };
  }

  return { '--dropdown-height': `${getContainerHeight() * 0.6}px` };
});

const dropdownTrigger = useTemplateRef('dropdown-trigger');

const accessibility = useAccessibleDropdown({
  onClose: closeDropdown,
  scrollBehavior: 'smooth',
  typeAheadTimeout: 500,
  wrapNavigation: true,
});

function toggleDropdown() {
  isDropdownOpen.value = !isDropdownOpen.value;
  if (isDropdownOpen.value) {
    nextTick(() => {
      focusListbox();
    });
  }
}

function closeDropdown() {
  isDropdownOpen.value = false;
  searchQuery.value = ''; // Clear search when closing
  accessibility.clearFocusedIndex();
  nextTick(() => {
    if (dropdownTrigger.value) {
      dropdownTrigger.value.focus();
    }
  });
}
function focusOnList() {
  const listbox = document.querySelector('.options-list');
  if (listbox) {
    listbox.focus();
  }
}

function focusListbox() {
  if (props.showSearch && searchInput.value) {
    searchInput.value.focus();
  } else {
    focusOnList();
  }
}

function handleTriggerKeydown(event) {
  accessibility.handleTriggerKeydown(event, isDropdownOpen.value, toggleDropdown);
}

function handleListboxKeydown(event) {
  accessibility.handleListboxKeydown(event, pillOptions.value, 'option', {
    onSelect: checkOption,
    onToggleSelection: checkOption,
    returnFocusSelector: '.dropdown-trigger',
    optionNameGetter: (option) => option.name,
    allowMultiSelect: props.allowMultiSelect,
  });
}

function handleListboxFocus() {
  if (selectedOptions.value.length > 0) {
    accessibility.handleListboxFocus(pillOptions.value, 'option', (option) => option.checked);
  }
}

function handleListboxBlur(event) {
  const relatedTarget = event.relatedTarget;

  if (relatedTarget && pillDropdown.value?.contains(relatedTarget)) {
    return;
  }

  setTimeout(() => {
    if (pillDropdown.value && !pillDropdown.value.contains(document.activeElement)) {
      closeDropdown();
    }
  }, 150);
}

onUnmounted(() => {
  accessibility.cleanup();
});

onClickOutside(pillDropdown, () => {
  if (isDropdownOpen.value) {
    closeDropdown();
  }
});

function clearFiltersFromRoute() {
  emit('clearFilters');
}

function checkOption(index) {
  // Find the actual option in the original array
  const filteredOption = pillOptions.value[index];
  const originalIndex = allOptions.value.findIndex((option) => {
    // Match by name and org_id (if it exists) or other unique identifiers
    if (option.org_id && filteredOption.org_id) {
      return option.name === filteredOption.name && option.org_id === filteredOption.org_id;
    } else if (option.type && filteredOption.type) {
      return option.name === filteredOption.name && option.type === filteredOption.type;
    } else {
      return option.name === filteredOption.name;
    }
  });
  if (originalIndex !== -1 && props.allowMultiSelect) {
    allOptions.value[originalIndex].checked = !allOptions.value[originalIndex].checked;
  }
  if (!props.allowMultiSelect) {
    const originalVal = allOptions.value[originalIndex].checked;
    allOptions.value.forEach((option) => {
      option.checked = false;
    });
    allOptions.value[originalIndex].checked = !originalVal;
    selectedOptions.value.length === 0 ? clearFiltersFromRoute() : applyFilters();
  }
}

function clearFilters() {
  appliedOptions.value = [];
  allOptions.value.forEach((option) => {
    option.checked = false;
  });

  clearFiltersFromRoute();

  nextTick(() => {
    const listbox = document.querySelector('.options-list');
    if (listbox) {
      listbox.focus();
      accessibility.setFocusedIndex(0, pillOptions.value.length, 'option');
    }
  });
}

function applyFilters() {
  appliedOptions.value = selectedOptions.value;
  emit('applyFilters');

  if (appliedOptions.value.length > 0) {
    emit('filterApplied', {
      filter_type: props.name.toLowerCase(),
      filter_value: appliedOptions.value.map((option) => option.name).toString(),
    });
  }

  closeDropdown();
}

const hasUnappliedChanges = computed(() => {
  const difference = useArrayDifference(
    selectedOptions.value,
    appliedOptions.value,
    (value, othVal) => value.name === othVal.name,
    { symmetric: true },
  );
  return difference.value.length > 0 || selectedOptions.value.length === 0;
});

const dynamicButtonName = computed(() => {
  if (appliedOptions.value.length >= 2) {
    return '2+ selected';
  } else if (appliedOptions.value.length === 1) {
    return appliedOptions.value[0].name;
  } else {
    return props.name;
  }
});

function handleActionButtonBlur(event) {
  accessibility.handleListboxBlur(event, pillDropdown, closeDropdown, [
    '.clear-button',
    '.apply-button',
    '.options-list',
  ]);
}
</script>

<style scoped lang="scss">
.pill-dropdown {
  --space-100: 4px;
  --space-150: 6px;
  --space-200: 8px;
  --space-300: 12px;
  --space-400: 16px;
  --color-blue-500: #1850c1;
  --color-white: #fff;
  --color-grey-100: #e0e0e0;
  --color-grey-200: #d3d3d3;
  --color-grey-950: #2d2d2d;
}

.dropdown-trigger {
  display: flex;
  border-radius: var(--secondary-button-border-radius);
  border: var(--secondary-button-border);
  background: var(--secondary-button-background);
  justify-content: center;
  align-items: center;
  padding: var(--space-150) var(--space-300);
  gap: var(--space-150);
  cursor: pointer;
  color: var(--secondary-button-text);

  &:focus-visible {
    outline: var(--secondary-button-focus-outline);
    outline-offset: 1px;
    border: var(--secondary-button-hover-border);
  }

  &:hover {
    background-color: var(--secondary-button-hover);
    border: var(--secondary-button-hover-border);
  }
  &.has-selected-vals {
    border-radius: var(--secondary-button-border-radius);
    border: var(--secondary-button-border);
    background: var(--secondary-button-active);
    color: var(--secondary-button-text);
  }
}

.dropdown-options {
  display: flex;
  align-items: flex-start;
  gap: var(--space-200);
  align-self: stretch;
  background: var(--color-white);
  position: relative;
}

.name {
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.1px;
}

.options-list {
  display: flex;
  align-items: flex-start;
  gap: var(--space-100);
  align-self: stretch;
  border-radius: var(--space-100);
  border: 1px solid var(--color-grey-100);
  box-sizing: border-box;
  z-index: 1;
  position: absolute;
  background: var(--color-white);
  max-height: var(--dropdown-height);
  min-width: 280px;
  top: 34px;
  flex-direction: column;

  &:focus-visible {
    outline: 2px solid var(--color-blue-500);
    outline-offset: -2px;
  }

  .clear-section {
    padding: var(--space-300) var(--space-400);
    display: flex;
    justify-content: space-between;
    box-sizing: border-box;
    border-top: 1px solid var(--color-grey-75);
    background: var(--color-white);
    width: 100%;

    button {
      padding: var(--space-200) var(--space-400);
      border: none;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      border-radius: var(--space-100);

      &:focus-visible {
        outline: 2px solid var(--color-blue-500);
        outline-offset: 2px;
      }

      &:hover:not(:disabled) {
        opacity: 0.8;
      }

      &:disabled {
        background: var(--disabled-button-background);
        color: var(--disabled-button-text-contrast);
        border: var(--disabled-button-border);
        cursor: default;
      }
    }

    .clear-button {
      background: var(--color-white);
      color: var(--tertiary-button-text-contrast);

      &:disabled {
        background: transparent;
        color: var(--disabled-button-tertiary-text-contrast);
      }
    }

    .apply-button {
      border-radius: var(--secondary-button-border-radius);
      background: var(--primary-button-background);
      color: var(--color-white);
    }
  }

  .sr-only {
    clip: rect(1px, 1px, 1px, 1px);
    clip-path: inset(50%);
    height: 1px;
    width: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
  }

  .search-section {
    padding: var(--space-300) var(--space-400) var(--space-100) var(--space-400);
    background: var(--color-white);
    width: -webkit-fill-available;
  }

  .search-box {
    display: flex;
    align-items: center;
    gap: var(--space-200);
    padding: var(--space-200) var(--space-300) var(--space-200) var(--space-300);
    border-radius: var(--border-radius-xs, 4px);
    border: 1px solid var(--color-grey-75, #e9e9e9);
    background: #f7f7f7;
    box-sizing: border-box;
    transition: border-color 0.2s ease;

    &:focus-within {
      outline: 2px solid var(--color-blue-500);
      outline-offset: -2px;
      border-color: var(--color-blue-500);
    }
  }

  .search-icon {
    color: #999;
    flex-shrink: 0;
  }

  .search-input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: var(--color-grey-950);
    background: transparent;

    &::placeholder {
      color: #999;
    }
  }
}

.options-list-ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-y: auto;
}

li {
  display: flex;
  align-items: center;
  list-style: none;
  padding: var(--space-300) var(--space-400);
  color: var(--color-grey-950);
  font-size: 12px;
  font-weight: 450;
  line-height: 16px;
  letter-spacing: 0.5px;
  cursor: pointer;
  position: relative;

  &:hover {
    background-color: var(--secondary-button-hover);
  }

  &.focused {
    background-color: var(--secondary-button-hover);
  }
}

li.show-checkbox[role='option']::before {
  position: relative;
  top: 1px;
  left: calc(-1 * var(--space-200));
  right: var(--space-100);
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='16' width='16' style='forced-color-adjust: auto;'%3E%3Crect x='2' y='2' height='13' width='13' rx='2' stroke='currentcolor' stroke-width='1' fill-opacity='0' /%3E%3C/svg%3E");
}

li.show-checkbox[role='option'][aria-checked='true']::before {
  position: relative;
  top: 1px;
  left: calc(-1 * var(--space-200));
  right: var(--space-100);
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='16' width='16' style='forced-color-adjust: auto;'%3E%3Crect x='2' y='2' height='13' width='13' rx='2' stroke='currentcolor' stroke-width='1' fill-opacity='0' /%3E%3Cpolyline points='4,8 7,12 12,5' fill='none' stroke='currentcolor' stroke-width='2' /%3E%3C/svg%3E");
}

@media (prefers-contrast: high) {
  .dropdown-trigger:focus,
  .options-list:focus {
    outline: 3px solid;
  }

  li.focused {
    border: 3px solid;
  }
}

@media (prefers-reduced-motion: reduce) {
  .options-list,
  li {
    transition: none;
  }
}
</style>
